import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { 
  AlertTriangleIcon,
  CheckCircleIcon,
  PlusIcon,
  MinusIcon,
  FileEditIcon
} from "lucide-react";
import {
  SelectedDataset,
  calculateSampledRequestCount,
  hasSessionSampling,
  PercentageVisualizer
} from "../CompositionDetail";
import CompositionReviewTable from "../CompositionReviewTable";

// Changes Summary Component for Edit Mode
interface ChangesSummaryProps {
  name: string;
  description: string;
  selectedDatasets: SelectedDataset[];
  originalComposition: any;
}

const ChangesSummary: React.FC<ChangesSummaryProps> = ({
  name,
  description,
  selectedDatasets,
  originalComposition,
}) => {
  if (!originalComposition) return null;

  // Check for basic info changes
  const nameChanged = name !== originalComposition.name;
  const descriptionChanged = description !== originalComposition.description;

  // Analyze dataset changes
  const originalDatasetIds = new Set(originalComposition.members.map((m: any) => m.origin_dataset_id));
  const newDatasetIds = new Set(selectedDatasets.map(d => d.dataset.id));
  
  const addedDatasets = selectedDatasets.filter(d => !originalDatasetIds.has(d.dataset.id));
  const removedDatasets = originalComposition.members.filter((m: any) => !newDatasetIds.has(m.origin_dataset_id));
  
  // Check for modified datasets (same dataset but different sampling)
  const modifiedDatasets = selectedDatasets.filter(newDataset => {
    const originalMember = originalComposition.members.find((m: any) => m.origin_dataset_id === newDataset.dataset.id);
    if (!originalMember) return false;
    
    return (
      originalMember.sampling_type.toLowerCase() !== newDataset.sampleType ||
      originalMember.sampling_weight !== newDataset.samplePercentage
    );
  });

  const hasChanges = nameChanged || descriptionChanged || addedDatasets.length > 0 || removedDatasets.length > 0 || modifiedDatasets.length > 0;

  if (!hasChanges) {
    return (
      <Card className="border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 mb-6">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <CheckCircleIcon className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="text-sm font-gc-med text-blue-900 dark:text-blue-100">No Changes Detected</h4>
              <p className="text-sm text-blue-700 dark:text-blue-200 mt-1">
                The composition settings are identical to the original. No changes will be made.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border mb-6">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-gc-med text-foreground">Summary of Changes</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Basic Info Changes */}
        {(nameChanged || descriptionChanged) && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <FileEditIcon className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-gc-med text-blue-600">Basic Information Updated</span>
            </div>
            <div className="ml-6 space-y-2">
              {nameChanged && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Name: </span>
                  <span className="line-through text-muted-foreground">{originalComposition.name}</span>
                  <span className="mx-2">→</span>
                  <span className="font-gc-med text-foreground">{name}</span>
                </div>
              )}
              {descriptionChanged && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Description: </span>
                  <span className="line-through text-muted-foreground max-w-[200px] inline-block truncate">
                    {originalComposition.description}
                  </span>
                  <span className="mx-2">→</span>
                  <span className="font-gc-med text-foreground max-w-[200px] inline-block truncate">
                    {description}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Added Datasets */}
        {addedDatasets.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <PlusIcon className="w-4 h-4 text-green-600" />
              <span className="text-sm font-gc-med text-green-600">
                {addedDatasets.length} Dataset{addedDatasets.length > 1 ? "s" : ""} Added
              </span>
            </div>
            <div className="ml-6 space-y-1">
              {addedDatasets.map(dataset => (
                <div key={dataset.dataset.id} className="text-sm">
                  <span className="font-gc-med text-foreground">{dataset.dataset.name}</span>
                  <span className="text-muted-foreground"> ({dataset.samplePercentage}% {dataset.sampleType} sampling)</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Removed Datasets */}
        {removedDatasets.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <MinusIcon className="w-4 h-4 text-red-600" />
              <span className="text-sm font-gc-med text-red-600">
                {removedDatasets.length} Dataset{removedDatasets.length > 1 ? "s" : ""} Removed
              </span>
            </div>
            <div className="ml-6 space-y-1">
              {removedDatasets.map((member: any) => (
                <div key={member.origin_dataset_id} className="text-sm">
                  <span className="line-through text-muted-foreground">{member.origin_dataset_name}</span>
                  <span className="text-muted-foreground"> (was {member.sampling_weight}% {member.sampling_type.toLowerCase()} sampling)</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Modified Datasets */}
        {modifiedDatasets.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <FileEditIcon className="w-4 h-4 text-orange-600" />
              <span className="text-sm font-gc-med text-orange-600">
                {modifiedDatasets.length} Dataset{modifiedDatasets.length > 1 ? "s" : ""} Modified
              </span>
            </div>
            <div className="ml-6 space-y-2">
              {modifiedDatasets.map(dataset => {
                const originalMember = originalComposition.members.find((m: any) => m.origin_dataset_id === dataset.dataset.id);
                return (
                  <div key={dataset.dataset.id}>
                    <div className="text-sm font-gc-med text-foreground">{dataset.dataset.name}</div>
                    <div className="text-sm ml-4">
                      <span className="text-muted-foreground">Sampling: </span>
                      <span className="line-through text-muted-foreground">{originalMember.sampling_weight}% {originalMember.sampling_type.toLowerCase()}</span>
                      <span className="mx-2">→</span>
                      <span className="font-gc-med text-foreground">{dataset.samplePercentage}% {dataset.sampleType}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// CompositionSummaryHeader Component
interface CompositionSummaryHeaderProps {
  name: string;
  description: string;
  selectedDatasets: SelectedDataset[];
}

const CompositionSummaryHeader: React.FC<CompositionSummaryHeaderProps> = ({
  name,
  description,
  selectedDatasets,
}) => {
  const totalRequests = selectedDatasets.reduce(
    (sum, item) => sum + calculateSampledRequestCount(item.dataset, item.samplePercentage), 
    0
  );
  
  const hasSessionSamplingEnabled = hasSessionSampling(selectedDatasets);

  return (
    <Card className="border-border mb-6">
      <CardContent className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <h2 className="text-xl font-gc-demi-bold text-foreground mb-2">{name}</h2>
            <p className="text-sm text-muted-foreground line-clamp-3">
              {description || "No description provided"}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-muted-foreground">Datasets</div>
              <div className="text-2xl font-gc-demi-bold text-blue-600">{selectedDatasets.length}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Total Requests</div>
              <div className="text-2xl font-gc-demi-bold text-green-600">
                {totalRequests.toLocaleString()}{hasSessionSamplingEnabled ? "*" : ""}
              </div>
            </div>
          </div>
        </div>
        {hasSessionSamplingEnabled && (
          <div className="text-xs text-muted-foreground mt-4">
            * Estimated count for session-based sampling
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// VisualOverviewPanel Component
interface VisualOverviewPanelProps {
  selectedDatasets: SelectedDataset[];
}

const VisualOverviewPanel: React.FC<VisualOverviewPanelProps> = ({ selectedDatasets }) => {
  const totalRequests = selectedDatasets.reduce(
    (sum, item) => sum + calculateSampledRequestCount(item.dataset, item.samplePercentage), 
    0
  );
  
  const originalRequests = selectedDatasets.reduce(
    (sum, item) => sum + item.dataset.request_count, 
    0
  );

  return (
    <Card className="border-border h-full min-h-[320px]">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-gc-med text-foreground">Visual Overview</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[170px] mb-6 px-5 flex justify-center items-center">
          <div className="w-4/5 h-full">
            <PercentageVisualizer selectedDatasets={selectedDatasets} hideLabel={true} />
          </div>
        </div>
        
        <div className="border-t border-border pt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-muted-foreground">Original Requests</div>
              <div className="text-base font-gc-med text-foreground">{originalRequests.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">After Sampling</div>
              <div className="text-base font-gc-med text-blue-600">
                {totalRequests.toLocaleString()}{hasSessionSampling(selectedDatasets) ? "*" : ""}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// DetailedBreakdownPanel Component
interface DetailedBreakdownPanelProps {
  selectedDatasets: SelectedDataset[];
  onConfigureDataset: (dataset: SelectedDataset) => void;
}

const DetailedBreakdownPanel: React.FC<DetailedBreakdownPanelProps> = ({ 
  selectedDatasets,
  onConfigureDataset
}) => {
  return (
    <Card className="border-border h-full">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-gc-med text-foreground">Detailed Breakdown</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="max-h-[340px] overflow-y-auto">
          <CompositionReviewTable
            selectedDatasets={selectedDatasets}
            onConfigureDataset={onConfigureDataset}
            emptyTitle="No datasets selected"
            emptyDescription="Add datasets to your composition to see them here."
          />
        </div>
        
        {hasSessionSampling(selectedDatasets) && (
          <div className="p-4 border-t border-border">
            <div className="text-xs text-muted-foreground">
              * Estimated count for session-based sampling
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ValidationFooter Component
interface ValidationFooterProps {
  selectedDatasets: SelectedDataset[];
  isEdit?: boolean;
}

const ValidationFooter: React.FC<ValidationFooterProps> = ({ 
  selectedDatasets,
  isEdit = false
}) => {
  // Check for potential issues
  const hasNoDatasets = selectedDatasets.length === 0;
  const hasLowPercentageDatasets = selectedDatasets.some(dataset => dataset.percentage < 5);
  
  return (
    <Card className="border-border mt-6">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            {hasNoDatasets ? (
              <div className="flex items-center gap-3">
                <AlertTriangleIcon className="w-5 h-5 text-red-600" />
                <span className="text-sm font-gc-med text-red-600">
                  No datasets selected. You must add at least one dataset.
                </span>
              </div>
            ) : hasLowPercentageDatasets ? (
              <div className="flex items-center gap-3">
                <AlertTriangleIcon className="w-5 h-5 text-orange-600" />
                <span className="text-sm font-gc-med text-orange-600">
                  Some datasets have a very low percentage. Consider adjusting the distribution.
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <CheckCircleIcon className="w-5 h-5 text-green-600" />
                <span className="text-sm font-gc-med text-green-600">
                  {isEdit 
                    ? "Your changes look good and are ready to be applied."
                    : "Your composition looks good and is ready to be created."
                  }
                </span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Main CompositionReview Component
interface CompositionReviewProps {
  name: string;
  description: string;
  selectedDatasets: SelectedDataset[];
  compositionType?: "SFT" | "RL";
  onConfigureDataset: (dataset: SelectedDataset) => void;
  onEditStep: () => void;
  isEdit?: boolean;
  originalComposition?: any;
}

const CompositionReview: React.FC<CompositionReviewProps> = ({
  name,
  description,
  selectedDatasets,
  compositionType: _compositionType = "SFT",
  onConfigureDataset,
  onEditStep: _onEditStep,
  isEdit = false,
  originalComposition,
}) => {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <h2 className="text-2xl font-gc-demi-bold text-foreground">
          {isEdit ? "Review Changes" : "Review Your Composition"}
        </h2>
        <p className="text-muted-foreground font-gc-reg">
          {isEdit 
            ? "Review the changes you're about to make to this composition."
            : "Review the details of your composition before creating it."
          }
        </p>
      </div>
      
      {/* Show changes summary for edit mode */}
      {isEdit && originalComposition && (
        <ChangesSummary
          name={name}
          description={description}
          selectedDatasets={selectedDatasets}
          originalComposition={originalComposition}
        />
      )}
      
      <CompositionSummaryHeader 
        name={name}
        description={description}
        selectedDatasets={selectedDatasets}
      />
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1">
          <VisualOverviewPanel selectedDatasets={selectedDatasets} />
        </div>
        <div className="lg:col-span-2">
          <DetailedBreakdownPanel 
            selectedDatasets={selectedDatasets}
            onConfigureDataset={onConfigureDataset}
          />
        </div>
      </div>
      
      <ValidationFooter 
        selectedDatasets={selectedDatasets}
        isEdit={isEdit}
      />
    </div>
  );
};

export default CompositionReview; 